import { format, fromUnixTime, getUnixTime } from 'date-fns';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import useTranslation from '../../../../hooks/useTranslations';
import { selectBusinessAreaById } from '../../../../redux/modules/data/businessAreasSlice';
import { selectFolderById } from '../../../../redux/modules/data/foldersSlice';
import { selectSystemLanguage } from '../../../../redux/modules/data/localizationSlice';
import { backupEvaluations, createEvaluation, evaluationsClearLatest, openEvaluationErrorModal, openEvaluationFinalizeModal, saveEvaluation, selectEvaluationById, selectEvaluationIsBackedUp, selectEvaluationIsRunning, selectEvaluationsLatestError, selectEvaluationsLatestSuccess } from '../../../../redux/modules/evaluationsSlice';
import Inputfield from '../../../misc/Inputfield';
import SelectBox from '../../../misc/SelectBox';
import Textarea from '../../../misc/Textarea';
import ContentWrapper from '../../layouts/partials/ContentWrapper';
import SidebarWrapper from '../../layouts/partials/SidebarWrapper';
import TableWrapper from '../../layouts/partials/TableWrapper';
import PrivateLayout from '../../layouts/PrivateLayout';

const EvaluationsForm = () => {
	const t = useTranslation();
	const dispatch = useDispatch();
	const history = useHistory();

	const {
		register, control, setValue, getValues, watch, formState: { errors }, trigger,
	} = useForm({ shouldUnregister: true, mode: 'all' });

	const systemLanguage = useSelector((state) => selectSystemLanguage(state));

	const { baId, id } = useParams();
	const businessArea = useSelector((state) => selectBusinessAreaById(state, baId));

	// Edit/Show
	const evaluation = useSelector((state) => selectEvaluationById(state, id));
	const isFormBackedUp = useSelector((state) => selectEvaluationIsBackedUp(state, id));

	// New
	const evaluationFolder = useSelector((state) => selectFolderById(state, businessArea.evaluationAppFolderId));
	const evaluationCategory = useSelector((state) => selectFolderById(state, id));

	// Save/Create Evaluation
	const isRunning = useSelector(selectEvaluationIsRunning);
	const latestSuccess = useSelector(selectEvaluationsLatestSuccess);
	const latestError = useSelector(selectEvaluationsLatestError);

	useEffect(() => {
		if (evaluation && evaluation.formValues) {
			Object.keys(evaluation.formValues).forEach((key) => {
				// TODO: Get translation for dropdowns
				setValue(key, evaluation.formValues[key]);
			});
		}
	}, [evaluation, setValue]);

	useEffect(() => {
		if (latestSuccess) {
			dispatch(evaluationsClearLatest());
			history.push(`/${baId}/evaluations/`);
		}
	}, [latestSuccess, dispatch, history, baId]);

	// TODO: Error Handling
	useEffect(() => {
		if (latestError) {
			dispatch(evaluationsClearLatest());
		}
	}, [latestError, dispatch]);

	if (!evaluation && !evaluationCategory) {
		return <Redirect to={`/${baId}/evaluations/`} />;
	}
	const evaluationForm = evaluation ? evaluation.form : evaluationCategory.evaluationSettings;

	const isFormFinalized = evaluation && evaluation.finalized;
	const isFormDisabled = isRunning || isFormFinalized;

	const handleFinalizeClick = async () => {
		const validationResult = await trigger();
		if (validationResult) {
			if (evaluation) {
				dispatch(openEvaluationFinalizeModal({ evaluationId: evaluation.id, form: evaluation.form, formValues: getValues() }));
			} else {
				dispatch(openEvaluationFinalizeModal({
					businessArea, evalAppFolder: evaluationFolder, evalAppCategory: evaluationCategory, form: evaluationCategory.evaluationSettings, formValues: getValues(),
				}));
			}
		} else {
			dispatch(openEvaluationErrorModal());
		}
	};

	const handleSaveClick = () => {
		if (evaluation) {
			dispatch(saveEvaluation({
				evaluationId: evaluation.id, form: evaluation.form, formValues: getValues(), finalized: false, t,
			}));
		} else {
			dispatch(createEvaluation({
				businessArea, evalAppFolder: evaluationFolder, evalAppCategory: evaluationCategory, form: evaluationCategory.evaluationSettings, formValues: getValues(), finalized: false, t,
			}));
		}
	};

	const handleUploadClick = () => {
		dispatch(backupEvaluations());
	};

	const handleDocumentClick = () => {
		window.open(process.env.REACT_APP_API_BASE_URL + process.env.REACT_APP_API_PATH_EVAL_APP_DOC + id, '_blank');
	};

	const mainButtons = [
		{ id: 1, title: t('eval_app_save'), handleClick: () => handleSaveClick() },
		{ id: 2, title: t('eval_app_form_finalize'), handleClick: () => handleFinalizeClick() },
	];

	const backupButton = [
		{ id: 1, title: t('webapp_upload'), handleClick: () => handleUploadClick() },
	];

	const documentButton = [
		{ id: 1, title: t('webapp_pdf_doc'), handleClick: () => handleDocumentClick() },
	];

	const breadcrumb = [
		{ route: baId, translationKey: businessArea.translationkey },
		{ route: `${baId}/evaluations`, translationKey: 'eval_app_evaluations' },
		{ route: `${baId}/evaluations/${evaluation ? evaluation.id : id}`, translationKey: evaluation ? evaluation.title : evaluationCategory.translationkey },
	];

	let buttons = documentButton;
	if (!isFormFinalized) {
		buttons = mainButtons;
	} else if (!isFormBackedUp) {
		buttons = backupButton;
	}

	const getTranslatedSectionTitle = (section) => {
		let sectionTitle = section.title;
		if (evaluationForm.translations && evaluationForm.translations[systemLanguage] && evaluationForm.translations[systemLanguage].sections[section.id] && evaluationForm.translations[systemLanguage].sections[section.id].title) {
			sectionTitle = evaluationForm.translations[systemLanguage].sections[section.id].title;
		}
		return sectionTitle;
	};

	const getTranslatedTitle = (formField) => {
		const asterisk = formField.required ? '*' : '';
		let { title } = formField;
		if (formField.alternativeAppTitle) {
			if (evaluationForm.translations && evaluationForm.translations[systemLanguage] && evaluationForm.translations[systemLanguage].fields[formField.id] && evaluationForm.translations[systemLanguage].fields[formField.id].alternativeAppTitle) {
				title = evaluationForm.translations[systemLanguage].fields[formField.id].alternativeAppTitle;
			} else {
				title = formField.alternativeAppTitle;
			}
		} else if (evaluationForm.translations && evaluationForm.translations[systemLanguage] && evaluationForm.translations[systemLanguage].fields[formField.id] && evaluationForm.translations[systemLanguage].fields[formField.id].title) {
			title = evaluationForm.translations[systemLanguage].fields[formField.id].title;
		}
		return title + asterisk;
	};

	const getTranslatedPlaceholder = (formField) => {
		if (isFormDisabled) {
			return '';
		}
		let { placeholder } = formField;
		if (evaluationForm.translations && evaluationForm.translations[systemLanguage] && evaluationForm.translations[systemLanguage].fields[formField.id] && evaluationForm.translations[systemLanguage].fields[formField.id].placeholder) {
			placeholder = evaluationForm.translations[systemLanguage].fields[formField.id].placeholder;
		}
		return placeholder;
	};

	const getTranslatedOptions = (formField) => {
		let { options } = formField;
		if (evaluationForm.translations && evaluationForm.translations[systemLanguage] && evaluationForm.translations[systemLanguage].fields[formField.id] && evaluationForm.translations[systemLanguage].fields[formField.id].options) {
			options = evaluationForm.translations[systemLanguage].fields[formField.id].options;
		}
		return options;
	};

	const isSectionHidden = (section) => {
		const fieldsShown = section.fields.some((field) => {
			if (field.evaluateHiddenBy) {
				const dropdownFieldId = Object.keys(field.evaluateHiddenBy)[0];
				const dropdownValue = watch(dropdownFieldId) ? watch(dropdownFieldId).value : null;
				if (!field.evaluateHiddenBy[dropdownFieldId].includes(dropdownValue)) {
					return false;
				}
			}
			return true;
		});
		return !fieldsShown;
	};

	const formHasRequiredQuestions = () => evaluationForm.sections.some((section) => section.fields.some((field) => field.required));

	return (
		<PrivateLayout customTree={breadcrumb}>
			<SidebarWrapper title={evaluation ? evaluation.title : t('eval_app_new_evaluation')} customTree={breadcrumb} />
			<ContentWrapper>
				<TableWrapper buttons={buttons} buttonsBottom>
					{formHasRequiredQuestions() && (
						<div className="px-6">
							<span className="text-[0.75rem] sm:text-[0.9375rem] font-semibold text-oly-gray-darkest">
								{t('eval_app_form_required_message')}
							</span>
						</div>
					)}
					<div className="flex flex-col py-4 lg:py-6 gap-y-14">
						{evaluationForm.sections.map((section) => {
							if (section.title !== 'Signature' && !isSectionHidden(section)) {
								return (
									<div key={section.id ?? uuidv4()}>
										<div className="px-6 border-oly-gray-light border-b-0.5 pb-4 mb-8">
											<span className="text-[1.125rem] sm:text-[1.5rem] font-semibold text-oly-gray-darkest">
												{getTranslatedSectionTitle(section)}
											</span>
										</div>
										<div className="flex flex-col px-4 gap-y-6">
											{section.fields.map((formField) => {
												if (formField.type === 'date') {
													if (formField.evaluateHiddenBy) {
														const dropdownFieldId = Object.keys(formField.evaluateHiddenBy)[0];
														const dropdownValue = watch(dropdownFieldId) ? watch(dropdownFieldId).value : null;
														if (!formField.evaluateHiddenBy[dropdownFieldId].includes(dropdownValue)) {
															return null;
														}
													}
													if (!getValues(formField.id)) {
														setValue(formField.id, format(fromUnixTime(getUnixTime(Date.now())), 'yyyy-MM-dd'));
													}
													// TODO: Uhrzeit?
													return <Inputfield disabled={isFormDisabled} key={formField.id} type="date" fieldName={formField.id} label={getTranslatedTitle(formField)} showLabel labelClasses="text-base font-semibold" placeholder={getTranslatedPlaceholder(formField)} backgroundColor="bg-white" register={register(formField.id, { required: formField.required ?? false })} error={errors[formField.id]} />;
												}
												if (formField.type === 'text') {
													if (formField.evaluateHiddenBy) {
														const dropdownFieldId = Object.keys(formField.evaluateHiddenBy)[0];
														const dropdownValue = watch(dropdownFieldId) ? watch(dropdownFieldId).value : null;
														if (!formField.evaluateHiddenBy[dropdownFieldId].includes(dropdownValue)) {
															return null;
														}
													}
													return <Inputfield disabled={isFormDisabled} key={formField.id} type="text" fieldName={formField.id} label={getTranslatedTitle(formField)} showLabel labelClasses="text-base font-semibold" placeholder={getTranslatedPlaceholder(formField)} backgroundColor="bg-white" register={register(formField.id, { required: formField.required ?? false })} error={errors[formField.id]} />;
												}
												if (formField.type === 'specify') {
													const dropdownFieldId = formField.id.replace('specify_', '');
													const dropdownValue = watch(dropdownFieldId) ? watch(dropdownFieldId).value : null;
													if (formField.placeholder && formField.placeholder !== dropdownValue) {
														return null;
													}
													return <Inputfield disabled={isFormDisabled} key={formField.id} type="text" fieldName={formField.id} label={getTranslatedTitle(formField)} showLabel labelClasses="text-base font-semibold" placeholder={getTranslatedPlaceholder(formField)} backgroundColor="bg-white" register={register(formField.id, { required: formField.required ?? false })} error={errors[formField.id]} />;
												}
												if (formField.type === 'textarea') {
													if (formField.evaluateHiddenBy) {
														const dropdownFieldId = Object.keys(formField.evaluateHiddenBy)[0];
														const dropdownValue = watch(dropdownFieldId) ? watch(dropdownFieldId).value : null;
														if (!formField.evaluateHiddenBy[dropdownFieldId].includes(dropdownValue)) {
															return null;
														}
													}
													return <Textarea disabled={isFormDisabled} key={formField.id} fieldName={formField.id} label={getTranslatedTitle(formField)} showLabel labelClasses="text-base font-semibold" placeholder={getTranslatedPlaceholder(formField)} backgroundColor="bg-white" register={register(formField.id, { required: formField.required ?? false })} error={errors[formField.id]} />;
												}
												if (formField.type === 'dropdown') {
													if (formField.evaluateHiddenBy) {
														const dropdownFieldId = Object.keys(formField.evaluateHiddenBy)[0];
														const dropdownValue = watch(dropdownFieldId) ? watch(dropdownFieldId).value : null;
														if (!formField.evaluateHiddenBy[dropdownFieldId].includes(dropdownValue)) {
															return null;
														}
													}
													return (
														<Controller
															key={formField.id}
															rules={{ required: formField.required ?? false }}
															control={control}
															name={formField.id}
															render={({ field }) => (
																<SelectBox
																	fieldName={formField.id}
																	label={getTranslatedTitle(formField)}
																	showLabel
																	labelClasses={`text-base font-semibold ${errors[formField.id] ? 'text-oly-red-default' : ''}`}
																	theme="white"
																	maxHeight={225}
																	options={Object(getTranslatedOptions(formField)).map((option, index) => ({ value: formField.options[index], label: option }))}
																	placeholder={getTranslatedPlaceholder(formField)}
																	field={field}
																	currentValue={getValues(formField.id)}
																	disabled={isFormDisabled}
																/>
															)}
														/>
													);
												}
												if (formField.type === 'multi') {
													if (formField.evaluateHiddenBy) {
														const dropdownFieldId = Object.keys(formField.evaluateHiddenBy)[0];
														const dropdownValue = watch(dropdownFieldId) ? watch(dropdownFieldId).value : null;
														if (!formField.evaluateHiddenBy[dropdownFieldId].includes(dropdownValue)) {
															return null;
														}
													}
													return (
														<Controller
															key={formField.id}
															rules={{ required: formField.required ?? false }}
															control={control}
															name={formField.id}
															render={({ field }) => (
																<SelectBox
																	fieldName={formField.id}
																	label={getTranslatedTitle(formField)}
																	showLabel
																	labelClasses={`text-base font-semibold ${errors[formField.id] ? 'text-oly-red-default' : ''}`}
																	theme="white"
																	maxHeight={225}
																	options={Object(getTranslatedOptions(formField)).map((option, index) => ({ value: `${formField.id}_${index}`, label: option }))}
																	placeholder={`${t('webapp_eval_multi_max')}: ${formField.maxMultiChoices ?? formField.options.length}`}
																	field={field}
																	currentValue={getValues(formField.id)}
																	isMulti
																	maxOptions={formField.maxMultiChoices}
																	disabled={isFormDisabled}
																/>
															)}
														/>
													);
												}
												if (formField.type === 'select') {
													if (formField.evaluateHiddenBy) {
														const dropdownFieldId = Object.keys(formField.evaluateHiddenBy)[0];
														const dropdownValue = watch(dropdownFieldId) ? watch(dropdownFieldId).value : null;
														if (!formField.evaluateHiddenBy[dropdownFieldId].includes(dropdownValue)) {
															return null;
														}
													}
													return (
														<fieldset key={formField.id}>
															<legend className={`block mx-1 text-base font-semibold ${errors[formField.id] ? 'text-oly-red-default' : 'text-oly-gray-darkest'}`}>
																{getTranslatedTitle(formField)}
															</legend>
															<div className="flex flex-wrap gap-1 mt-2">
																{getTranslatedOptions(formField).map((option, index) => (
																	<label key={option} htmlFor={`${formField.id}-${index}`} className={`flex items-center justify-center w-full xl:flex-1 text-[0.875rem] text-oly-gray-darkest bg-white rounded-md focus:outline-none ${!isFormDisabled ? 'hover:font-medium hover:bg-gray-50 cursor-pointer' : ''}`}>
																		<input disabled={isFormDisabled} type="radio" id={`${formField.id}-${index}`} name={`${formField.id}-${index}`} value={formField.options[index]} className="sr-only eval-radio" aria-labelledby={`${formField.id}-${index}-label`} {...register(formField.id, { required: formField.required ?? false })} />
																		<p id={`${formField.id}-${index}-label`} className={`w-full h-full px-3 py-2 text-center truncate rounded-md ${!isFormDisabled ? '' : 'opacity-50'}`}>
																			{option}
																		</p>
																	</label>
																))}
															</div>
														</fieldset>
													);
												}
												return null;
											})}
										</div>
									</div>
								);
							}
							return null;
						})}
					</div>
				</TableWrapper>
			</ContentWrapper>
		</PrivateLayout>
	);
};

export default EvaluationsForm;
